import { Avatar, AvatarSize, LabelChipVariant, TableCell, theme } from '@ketch-com/deck'
import { Classification, ClassificationType, EventListItem } from '@ketch-com/windlass/dist/dataSentry/v1/events/events'
import { Box, Typography } from '@mui/material'
import { GridColDef, GridColumnHeaderParams, GridRenderCellParams } from '@mui/x-data-grid-premium'
import { sortAlphabetically } from '../../../dataSystems/DataSystem/assetsTab/utils/sortAlphabetically'
import { EmptyValueRenderer } from 'components/EmptyValueRenderer'
import { AssetsTabTableLabelChip } from 'pages/dataSystems/DataSystem/assetsTab/components/AssetsTabTableLabelChip'
import {
  CalssificationExpansionDropdown,
  ClassificationIdString,
} from 'pages/dataSystems/DataSystem/assetsTab/components/ClassificationExpansionDropdown'
import { DataClassificationDetails } from 'pages/dataSentry/interfaces'

// number of colors = data types / total data types and length = even counts over
export const getSpectrum = (count: number, totalCount: number) => {
  const percentage = (count / totalCount) * 100
  if (percentage > 15) {
    return 'linear-gradient(90deg, #40FF00 0%, #00F1D4 20%, #0077FF 40%, #FB2AFF 55%, #FF020D 70%, #FF6A00 85%, #FFD900 100%)'
  } else if (percentage > 8) {
    return 'linear-gradient(90deg, #40FF00 0%, #00F1D4 20%, #0077FF 40%, #FB2AFF 60%, #FF020D 80%, #FF6A00 100%)'
  } else if (percentage > 3) {
    return 'linear-gradient(90deg, #40FF00 0%, #00F1D4 25%, #0077FF 50%, #FB2AFF 75%, #FF020D 100%)'
  } else if (percentage > 1) {
    return 'linear-gradient(90deg, #40FF00 0%, #00F1D4 33%, #0077FF 66%, #FB2AFF 100%)'
  } else {
    return 'linear-gradient(90deg, #40FF00 0%, #00F1D4 50%, #0077FF 100%)'
  }
}

export interface DataSentryEventsDataGridUtilProps {
  dataTypesTotalCount?: number
  setDataClassificationDetails?: React.Dispatch<React.SetStateAction<DataClassificationDetails | null>>
}

export const dataSentryEventsDataGridUtil = ({
  dataTypesTotalCount,
  setDataClassificationDetails = () => {},
}: DataSentryEventsDataGridUtilProps): GridColDef<EventListItem>[] => {
  const columns: GridColDef<EventListItem>[] = [
    {
      align: 'left',
      field: 'collectorUrl',
      headerName: 'Collector URL',
      minWidth: 450,
      flex: 2,
      headerAlign: 'left',
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant="smallLabel" color={theme.palette.Text.Secondary}>
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: ({ row }: GridRenderCellParams<EventListItem>) => {
        return (
          <TableCell>
            <Box display="flex" flexDirection="column">
              <Typography component="div" variant="label">
                {row.url}
              </Typography>
              {row.dataSystem && (
                <Box display="flex" alignItems="center" gap={0.5}>
                  {row.dataSystem?.logoUrl && <Avatar src={row.dataSystem?.logoUrl} size={AvatarSize.small} />}
                  <Typography variant="smallBody">{row.dataSystem?.name}</Typography>
                </Box>
              )}
            </Box>
          </TableCell>
        )
      },
    },
    {
      align: 'left',
      field: 'datacategories',
      headerName: 'Data Categories',
      headerAlign: 'left',
      minWidth: 450,
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant="smallLabel" color={theme.palette.Text.Secondary}>
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: ({ row }: GridRenderCellParams<EventListItem>) => {
        let displayItems
        let dropDownListItems: Classification[] = []
        const sortedDataCategoriesList = sortAlphabetically(
          row.classificationsByType?.find(
            classificaionsLists => classificaionsLists.classificationType === ClassificationType.DATA_CATEGORY,
          )?.classifications || [],
          'name',
        )
        if (sortedDataCategoriesList.length <= 2) {
          displayItems = sortedDataCategoriesList
        } else {
          displayItems = sortedDataCategoriesList.slice(0, 2) as Classification[]
          dropDownListItems = sortedDataCategoriesList.slice(2) as Classification[]
        }

        return (
          <TableCell>
            {displayItems && displayItems.length ? (
              <>
                {displayItems.map((classification: Classification) => (
                  <Box
                    key={classification.code}
                    onClick={() => {
                      setDataClassificationDetails?.({
                        type: ClassificationType.DATA_CATEGORY,
                        name: classification.name || '',
                        code: classification.code || '',
                      })
                    }}
                  >
                    <AssetsTabTableLabelChip
                      key={classification.code}
                      title={classification.name || classification.code}
                      variant={LabelChipVariant.category}
                      hideConfidence
                      hideIcon
                      isViewOnly
                    />
                  </Box>
                ))}
                {dropDownListItems.length > 0 && (
                  <CalssificationExpansionDropdown
                    label={`+${dropDownListItems.length} more`}
                    items={dropDownListItems}
                    labelChipVariant={LabelChipVariant.category}
                    idKeyValue={ClassificationIdString.assetDataCategoryId}
                    isViewOnly
                    hideIcon
                    handleManualClassificationVerification={() => {}}
                  />
                )}
              </>
            ) : (
              <EmptyValueRenderer />
            )}
          </TableCell>
        )
      },
    },
  ]
  return columns
}
